'use client';

import { useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  PREVENTION_WAF_VENDORS,
  type CreatePreventionTargetMappingBody,
  type PreventionConnectionDto,
  type PreventionTargetMappingDto,
  type PreventionWafSuggestion,
} from '@terra/shared-contracts-integrations';
import { toasterApi } from '../../toast/ToastProvider';
import {
  buildAwsWebAclMappingBody,
  buildConnectionMappingBody,
  buildRoutingTargetOptions,
  EMPTY_PREVENTION_ROUTING_DRAFT,
  filterRoutableConnections,
  findApplicationMapping,
  findRecommendedWebAclArn,
  isPreventionSuggestionApplied,
  PREVENTION_ROUTING_TARGETS,
  resolveCloudflareConnectionForSuggestion,
  resolvePreventionDiscoveryRequest,
  resolvePreventionRoutingState,
  resolveVercelConnectionForSuggestion,
  toPreventionRoutingTarget,
  type PreventionRoutingDraft,
  type PreventionRoutingTarget,
} from './prevention-routing.model';

export type UseApplicationPreventionRoutingParams = {
  applicationId: string;
  connections: readonly PreventionConnectionDto[];
  mappings: readonly PreventionTargetMappingDto[];
  suggestions: readonly PreventionWafSuggestion[];
  isAwsPreventionReady: boolean;
  onCreateMapping: (
    body: CreatePreventionTargetMappingBody,
  ) => Promise<unknown>;
  onDeleteMapping: (params: { mappingId: string }) => Promise<unknown>;
  onEnsureAwsWafConnection: () => Promise<{ id: string } | null | undefined>;
  onRefetchMappings: () => Promise<unknown>;
};

const toConnectionOptions = (connections: readonly PreventionConnectionDto[]) =>
  connections.map((connection) => ({
    value: connection.id,
    label: connection.label,
  }));

export function useApplicationPreventionRouting({
  applicationId,
  connections,
  mappings,
  suggestions,
  isAwsPreventionReady,
  onCreateMapping,
  onDeleteMapping,
  onEnsureAwsWafConnection,
  onRefetchMappings,
}: UseApplicationPreventionRoutingParams) {
  const t = useTranslations('designSystem.applicationSettings.prevention');
  const [draft, setDraft] = useState<PreventionRoutingDraft>(
    EMPTY_PREVENTION_ROUTING_DRAFT,
  );
  const [isSuggestionHidden, setIsSuggestionHidden] = useState(false);
  const currentMapping = useMemo(
    () => findApplicationMapping({ mappings, applicationId }),
    [applicationId, mappings],
  );
  const cloudflareConnections = useMemo(
    () =>
      filterRoutableConnections({
        connections,
        vendor: PREVENTION_WAF_VENDORS.CLOUDFLARE,
      }),
    [connections],
  );
  const vercelConnections = useMemo(
    () =>
      filterRoutableConnections({
        connections,
        vendor: PREVENTION_WAF_VENDORS.VERCEL,
      }),
    [connections],
  );
  const currentConnection = useMemo(
    () =>
      connections.find(
        (connection) => connection.id === currentMapping?.connectionId,
      ) ?? null,
    [connections, currentMapping],
  );
  const targetOptions = useMemo(
    () =>
      buildRoutingTargetOptions({
        hasCloudflare: cloudflareConnections.length > 0,
        hasVercel: vercelConnections.length > 0,
        isAwsPreventionReady,
      }),
    [
      cloudflareConnections.length,
      isAwsPreventionReady,
      vercelConnections.length,
    ],
  );
  const defaultTarget =
    toPreventionRoutingTarget(currentConnection?.vendor) ??
    targetOptions[0] ??
    null;
  const state = resolvePreventionRoutingState({
    draft,
    defaultTarget,
    currentMapping,
    currentConnection,
    cloudflareConnections,
    vercelConnections,
  });
  const topSuggestion = suggestions[0] ?? null;
  const isSuggestionApplied = isPreventionSuggestionApplied({
    suggestion: topSuggestion,
    state,
    currentMapping,
    currentConnection,
  });
  const hasAnyRoutingOption = targetOptions.length > 0;
  const discoveryRequest = resolvePreventionDiscoveryRequest({
    state,
    isAwsPreventionReady,
  });

  const selectTarget = useCallback((target: PreventionRoutingTarget | null) => {
    setDraft({ ...EMPTY_PREVENTION_ROUTING_DRAFT, selectedTarget: target });
  }, []);
  const selectCloudflareConnection = useCallback((connectionId: string) => {
    setDraft((previous) => ({
      ...previous,
      selectedConnectionId: connectionId,
      zoneId: '',
    }));
  }, []);
  const selectVercelConnection = useCallback((connectionId: string) => {
    setDraft((previous) => ({
      ...previous,
      selectedConnectionId: connectionId,
      projectId: '',
    }));
  }, []);
  const setZoneId = useCallback((zoneId: string) => {
    setDraft((previous) => ({ ...previous, zoneId }));
  }, []);
  const setProjectId = useCallback((projectId: string) => {
    setDraft((previous) => ({ ...previous, projectId }));
  }, []);
  const setWebAclArn = useCallback((webAclArn: string) => {
    setDraft((previous) => ({ ...previous, webAclArn }));
  }, []);

  const applySuggestion = useCallback(
    (suggestion: PreventionWafSuggestion) => {
      const target = toPreventionRoutingTarget(suggestion.vendor);
      const scopeRef = suggestion.suggestedScopeRef;

      if (target === PREVENTION_ROUTING_TARGETS.CLOUDFLARE) {
        const connection = resolveCloudflareConnectionForSuggestion({
          suggestion,
          connections: cloudflareConnections,
          currentConnectionId: currentMapping?.connectionId,
        });

        setDraft((previous) => ({
          ...previous,
          selectedTarget: target,
          selectedConnectionId: connection?.id ?? previous.selectedConnectionId,
          zoneId: scopeRef ?? previous.zoneId,
        }));

        return;
      }

      if (target === PREVENTION_ROUTING_TARGETS.VERCEL) {
        const connection = resolveVercelConnectionForSuggestion({
          connections: vercelConnections,
          currentConnectionId: currentMapping?.connectionId,
        });

        setDraft((previous) => ({
          ...previous,
          selectedTarget: target,
          selectedConnectionId: connection?.id ?? previous.selectedConnectionId,
          projectId: scopeRef ?? previous.projectId,
        }));

        return;
      }

      if (target === PREVENTION_ROUTING_TARGETS.AWS_WAFV2) {
        setDraft((previous) => ({
          ...previous,
          selectedTarget: target,
          webAclArn: scopeRef ?? previous.webAclArn,
        }));
      }
    },
    [cloudflareConnections, currentMapping, vercelConnections],
  );

  const resolveMappingBody = async () => {
    if (state.effectiveTarget !== PREVENTION_ROUTING_TARGETS.AWS_WAFV2) {
      return buildConnectionMappingBody({ applicationId, state });
    }

    if (!state.effectiveWebAclArn.trim()) {
      return null;
    }

    const awsConnection = await onEnsureAwsWafConnection();

    if (!awsConnection) {
      throw new Error(t('awsEnsureFailed'));
    }

    return buildAwsWebAclMappingBody({
      applicationId,
      connectionId: awsConnection.id,
      webAclArn: state.effectiveWebAclArn,
    });
  };

  const save = async () => {
    if (!state.effectiveTarget) {
      return;
    }

    try {
      const body = await resolveMappingBody();

      if (!body) {
        return;
      }

      await onCreateMapping(body);
      await onRefetchMappings();
      setDraft(EMPTY_PREVENTION_ROUTING_DRAFT);
      toasterApi.success({ title: t('savedTitle') });
    } catch (error) {
      console.error(new Error('Failed to save prevention routing'), error);
      toasterApi.error({
        title: t('saveFailedTitle'),
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const clear = async () => {
    if (!currentMapping) {
      setDraft((previous) => ({
        ...previous,
        zoneId: '',
        projectId: '',
        webAclArn: '',
      }));

      return;
    }

    try {
      await onDeleteMapping({ mappingId: currentMapping.id });
      await onRefetchMappings();
      setDraft((previous) => ({
        ...EMPTY_PREVENTION_ROUTING_DRAFT,
        projectId: previous.projectId,
      }));
      toasterApi.success({ title: t('removedTitle') });
    } catch (error) {
      console.error(new Error('Failed to remove prevention routing'), error);
      toasterApi.error({
        title: t('removeFailedTitle'),
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  return {
    state,
    currentMapping,
    currentConnection,
    targetOptions,
    hasAnyRoutingOption,
    cloudflareConnectionOptions: toConnectionOptions(cloudflareConnections),
    vercelConnectionOptions: toConnectionOptions(vercelConnections),
    topSuggestion: isSuggestionHidden ? null : topSuggestion,
    isSuggestionApplied,
    recommendedWebAclArn: findRecommendedWebAclArn(suggestions),
    discoveryRequest,
    isAwsPreventionReady,
    selectTarget,
    selectCloudflareConnection,
    selectVercelConnection,
    setZoneId,
    setProjectId,
    setWebAclArn,
    applySuggestion,
    hideSuggestion: () => setIsSuggestionHidden(true),
    save,
    clear,
  };
}

export type ApplicationPreventionRouting = ReturnType<
  typeof useApplicationPreventionRouting
>;
